import React, { useState, Children } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastProvider';
import { useAuth } from '../contexts/AuthContext';
import { CheckSquareIcon, PlusIcon, CalendarIcon, UsersIcon, BookOpenIcon, FilterIcon, SearchIcon, ChevronDownIcon } from 'lucide-react';
import { TreeView } from '../components/TreeView';
import { MOCK_HIERARCHY, LETGROW_HIERARCHY, LETGROW_EXERCISES_DETAILED, MOCK_EXERCISE_ASSIGNMENTS } from '../schemas/mockData';
import type { HierarchyNode, ExerciseAssignment, ExerciseDefinition } from '../schemas/types';
/**
 * AssignmentManagement Page
 * Manages exercise assignments to hierarchy nodes with TreeView integration
 * Supports flexible deployment: organization, school, grade-band, or cohort level
 */
export function AssignmentManagement() {
  const {
    language,
    currentOrganization,
    getHierarchyLabels,
    t
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const {
    isAdmin
  } = useAuth();
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set(['node-org-kmg', 'node-org-letgrow']));
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const [dueDate, setDueDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'active' | 'completed'>('all');
  const hierarchyLabels = getHierarchyLabels();
  // Get hierarchy based on current organization
  const hierarchy = currentOrganization.id === 'org-letgrow' ? LETGROW_HIERARCHY : MOCK_HIERARCHY;
  // Get available exercises
  const availableExercises: ExerciseDefinition[] = currentOrganization.id === 'org-letgrow' ? LETGROW_EXERCISES_DETAILED : [{
    exerciseId: 'ex-ukrainian-kids',
    title: {
      en: 'Ukrainian Children Wellbeing',
      uk: 'Добробут українських дітей'
    },
    sets: {
      A: 'emotional-faces-set-a',
      B: 'emotional-faces-set-b'
    },
    timing: {
      perImageMs: 1800,
      interStimulusMs: 500,
      maxSetSeconds: 180
    },
    primingCopy: {
      A: {
        en: 'I wish I could feel more...',
        uk: 'Я хотів би відчувати більше...'
      },
      B: {
        en: 'I wish I could feel less...',
        uk: 'Я хотів би відчувати менше...'
      }
    },
    algorithmVersion: 'ALG-1.2.0',
    interpretationTemplate: 'TPL-UK-1.0.0',
    locales: ['en', 'uk']
  }];
  // Get assignments for selected node
  const nodeAssignments = selectedNode ? MOCK_EXERCISE_ASSIGNMENTS.filter(a => a.nodeId === selectedNode.id) : [];
  // Filter assignments
  const filteredAssignments = nodeAssignments.filter(assignment => {
    if (filterType === 'active') return assignment.status === 'active';
    if (filterType === 'completed') return assignment.status === 'completed';
    return true;
  });
  const handleToggleExpand = (nodeId: string) => {
    setExpandedNodeIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };
  const handleAssignExercise = () => {
    if (!selectedNode || !selectedExercise) return;
    // In production, this would make an API call
    const newAssignment: ExerciseAssignment = {
      id: `assign-${Date.now()}`,
      exerciseId: selectedExercise,
      nodeId: selectedNode.id,
      assignedBy: 'current-user',
      assignedAt: new Date().toISOString(),
      dueDate: dueDate || undefined,
      status: 'active'
    };
    MOCK_EXERCISE_ASSIGNMENTS.push(newAssignment);
    setShowAssignModal(false);
    setSelectedExercise('');
    setDueDate('');
  };
  const getExerciseTitle = (exerciseId: string): string => {
    const exercise = availableExercises.find(ex => ex.exerciseId === exerciseId);
    if (!exercise) return exerciseId;
    return exercise.title[language] || exercise.title.en || exerciseId;
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-721">
      {/* Header */}
      <div className="mb-8" data-id="element-722">
        <div className="flex items-center gap-3 mb-3" data-id="element-723">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-724">
            <CheckSquareIcon className="w-7 h-7 text-white" data-id="element-725" />
          </div>
          <div data-id="element-726">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-727">
              {t.assignmentManagement}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-728">
              {t.assignmentManagementSubtitle}
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6" data-id="element-729">
        {/* Left Panel: Hierarchy Tree */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-730">
          <div className="flex items-center justify-between mb-6" data-id="element-731">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-732">
              {t.organizationHierarchy}
            </h2>
            <div className="text-sm text-gray-600" data-id="element-733">{t.selectNodeToAssign}</div>
          </div>
          <div className="space-y-2 max-h-[600px] overflow-y-auto" data-id="element-734">
            <TreeView node={hierarchy.rootNode} onNodeClick={setSelectedNode} selectedNodeId={selectedNode?.id} expandedNodeIds={expandedNodeIds} onToggleExpand={handleToggleExpand} showActions={false} data-id="element-735" />
          </div>
        </div>
        {/* Right Panel: Assignments */}
        <div className="space-y-6" data-id="element-736">
          {/* Selected Node Info */}
          {selectedNode ? <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200" data-id="element-737">
              <div className="flex items-start justify-between mb-4" data-id="element-738">
                <div data-id="element-739">
                  <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-740">
                    {selectedNode.name}
                  </h3>
                  <p className="text-sm text-gray-600 capitalize" data-id="element-741">
                    {selectedNode.type.replace('-', ' ')}
                  </p>
                  {selectedNode.metadata?.participantCount && <div className="flex items-center gap-2 mt-2 text-sm text-gray-700" data-id="element-742">
                      <UsersIcon className="w-4 h-4" data-id="element-743" />
                      <span data-id="element-744">
                        {selectedNode.metadata.participantCount}{' '}
                        {t.participants}
                      </span>
                    </div>}
                </div>
                <button onClick={() => setShowAssignModal(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm" data-id="element-745">
                  <PlusIcon className="w-4 h-4" data-id="element-746" />
                  {t.assignExercise}
                </button>
              </div>
            </div> : <div className="bg-gray-50 rounded-xl p-12 border-2 border-dashed border-gray-300 text-center" data-id="element-747">
              <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-748" />
              <p className="text-gray-600" data-id="element-749">{t.selectFromHierarchy}</p>
            </div>}
          {/* Assignments List */}
          {selectedNode && <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-750">
              <div className="flex items-center justify-between mb-6" data-id="element-751">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-752">
                  {t.assignedExercises}
                </h3>
                <div className="flex items-center gap-2" data-id="element-753">
                  <select value={filterType} onChange={e => setFilterType(e.target.value as any)} className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-754">
                    <option value="all" data-id="element-755">{t.all}</option>
                    <option value="active" data-id="element-756">{t.active}</option>
                    <option value="completed" data-id="element-757">{t.completed}</option>
                  </select>
                </div>
              </div>
              {filteredAssignments.length > 0 ? <div className="space-y-3" data-id="element-758">
                  {filteredAssignments.map(assignment => <div key={assignment.id} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-colors" data-id="element-759">
                      <div className="flex items-start justify-between" data-id="element-760">
                        <div className="flex-1" data-id="element-761">
                          <div className="flex items-center gap-2 mb-2" data-id="element-762">
                            <BookOpenIcon className="w-4 h-4 text-blue-600" data-id="element-763" />
                            <span className="font-semibold text-gray-900" data-id="element-764">
                              {getExerciseTitle(assignment.exerciseId)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1" data-id="element-765">
                            <div data-id="element-766">
                              {t.assigned}:{' '}
                              {new Date(assignment.assignedAt).toLocaleDateString()}
                            </div>
                            {assignment.dueDate && <div className="flex items-center gap-1" data-id="element-767">
                                <CalendarIcon className="w-3 h-3" data-id="element-768" />
                                {t.dueDate}:{' '}
                                {new Date(assignment.dueDate).toLocaleDateString()}
                              </div>}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${assignment.status === 'active' ? 'bg-green-100 text-green-700' : assignment.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-769">
                          {assignment.status}
                        </span>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-12 text-gray-500" data-id="element-770">
                  <BookOpenIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" data-id="element-771" />
                  <p data-id="element-772">
                    {filterType !== 'all' ? t.noFilteredExercises : t.noAssignedExercises}
                  </p>
                </div>}
            </div>}
        </div>
      </div>
      {/* Assign Exercise Modal */}
      {showAssignModal && selectedNode && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-773">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" data-id="element-774">
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-775">
              {t.assignExercise}
            </h3>
            <p className="text-sm text-gray-600 mb-6" data-id="element-776">
              {t.assigningTo}:{' '}
              <span className="font-semibold" data-id="element-777">{selectedNode.name}</span>
            </p>
            <div className="space-y-4 mb-6" data-id="element-778">
              <div data-id="element-779">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-780">
                  {t.selectExercise}
                </label>
                <select value={selectedExercise} onChange={e => setSelectedExercise(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-781">
                  <option value="" data-id="element-782">{t.chooseExercise}</option>
                  {availableExercises.map(exercise => <option key={exercise.exerciseId} value={exercise.exerciseId} data-id="element-783">
                      {exercise.title[language] || exercise.title.en}
                    </option>)}
                </select>
              </div>
              <div data-id="element-784">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-785">
                  {t.dueDateOptional}
                </label>
                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-786" />
              </div>
            </div>
            <div className="flex gap-3" data-id="element-787">
              <button onClick={() => setShowAssignModal(false)} className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold" data-id="element-788">
                {t.cancel}
              </button>
              <button onClick={handleAssignExercise} disabled={!selectedExercise} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-789">
                {t.assignExercise}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}